/**
 * Fax API integration (Notifyre).
 * All endpoints require authentication; tokens are never exposed to frontend.
 */
import api from './axios';

const FAXES_BASE = 'faxes';
const CONFIG_BASE = 'fax-config';

/**
 * List fax transmissions for the current provider.
 * @returns {Promise<Array>} List of fax transmission objects
 */
export async function listFaxes() {
  const { data } = await api.get(`/${FAXES_BASE}/`);
  return Array.isArray(data) ? data : data.results || [];
}

/**
 * Get a single fax transmission by ID.
 * @param {string} id - UUID of the fax transmission
 * @returns {Promise<Object>} Fax transmission object
 */
export async function getFax(id) {
  const { data } = await api.get(`/${FAXES_BASE}/${id}/`);
  return data;
}

/**
 * Send a fax (create transmission and enqueue job).
 * @param {Object} payload - { document_id: string (appeal UUID), to_number: string (E.164), from_number?: string }
 * @returns {Promise<Object>} Created fax transmission
 */
export async function sendFax(payload) {
  const { data } = await api.post(`/${FAXES_BASE}/`, payload);
  return data;
}

/**
 * Retry a failed fax.
 * @param {string} id - UUID of the fax transmission
 * @returns {Promise<Object>} Updated fax transmission
 */
export async function retryFax(id) {
  const { data } = await api.post(`/${FAXES_BASE}/${id}/retry/`);
  return data;
}

/**
 * Cancel a queued/sending fax (soft cancel).
 * @param {string} id - UUID of the fax transmission
 */
export async function cancelFax(id) {
  await api.delete(`/${FAXES_BASE}/${id}/`);
}

/**
 * Get fax integration config for the current provider (read-only; no secret keys).
 * Returns default_sender_number, is_enabled, etc. Config is set server-side only.
 * @returns {Promise<Array>} List of config objects (usually one for provider)
 */
export async function getFaxConfig() {
  const { data } = await api.get(`/${CONFIG_BASE}/`);
  return Array.isArray(data) ? data : data.results || [];
}
